import type { Course, Project } from '@rcs/data-schema';
import {
  addCourse,
  createSession,
  duplicateCourse,
  neighborAfterRemoval,
  reduceSession,
  removeCourse,
  replaceCourse,
  sessionState,
  type EditorSession,
  type EditorState,
  type SessionAction,
} from '@rcs/editor';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

/** {@link useCourses} の戻り値。 */
export interface CoursesState {
  /** 編集中のコースを反映したプロジェクト（一覧・保存・書き出しに使う） */
  readonly project: Project;
  /** 編集の一続き（履歴とドラッグ） */
  readonly session: EditorSession;
  /** 編集中のコースと選択 */
  readonly editor: EditorState;
  /** 編集中のコースへの操作 */
  readonly dispatch: (action: SessionAction) => void;
  /** 最後に保存してから、変更があるか */
  readonly dirty: boolean;
  /** コースを切り替える（編集内容はプロジェクトに残す） */
  readonly select: (id: string) => void;
  /** コースを、編集中のコースの次に追加して切り替える */
  readonly add: (course: Course) => void;
  /** 編集中のコースを複製し、複製を編集する */
  readonly duplicate: (newId: string, now: string) => void;
  /** コースを削除する（編集中なら、隣のコースへ切り替える） */
  readonly remove: (id: string) => void;
  /** プロジェクトを丸ごと置き換える（読み込み）。先頭のコースを編集する。未保存の扱い */
  readonly replaceProject: (project: Project) => void;
  /**
   * 保存を始める：そのときのプロジェクトの構成（コースの追加・削除・切り替え・読み込み）の版を返す。
   * 保存が終わったら、この版を `markSaved` に渡す。
   */
  readonly beginSave: () => number;
  /**
   * 保存が終わった。保存したコースを未保存の基準にする（保存中に別のコースへ切り替えていれば、
   * 基準は変えない）。保存中にプロジェクトの構成が変わっていれば（版が違えば）、未保存のままにする。
   * 保存中の操作を打ち消さないよう、保存したプロジェクトでコースの一覧を置き換えない。
   */
  readonly markSaved: (course: Course, version: number) => void;
}

/**
 * プロジェクトの複数のコースと、編集中のコースの状態（FR-CRS-001, 002）。
 *
 * 編集中のコースはエディターの履歴（`@rcs/editor` のセッション）で持ち、切り替えるときに
 * プロジェクトへ書き戻す。取り消し・やり直しの履歴は、コースを切り替えると作り直す。
 * 選択中のコースは保存データに入れない（エディターの一時状態）。
 *
 * @param initialCourse 最初に編集するコース（既定：先頭のコース）
 */
export function useCourses(initialProject: Project, initialCourse?: Course): CoursesState {
  const [stored, setStored] = useState(initialProject);
  const [session, dispatch] = useReducer(reduceSession, undefined, () => {
    const course = initialCourse ?? initialProject.courses[0];
    if (!course) throw new Error('プロジェクトにコースがありません');
    return createSession(course);
  });
  const editor = sessionState(session);
  // 未保存の判定：編集中のコースが基準から変わった、またはプロジェクト（コースの追加・削除など）が変わった
  const [baseline, setBaseline] = useState(() => editor.course);
  const [projectDirty, setProjectDirty] = useState(false);
  const courseDirty = editor.course !== baseline;
  // 保存の途中で、コースの追加・削除・切り替えなどがあったかを判定する版と、編集中のコースの ID
  const structure = useRef(0);
  const currentId = useRef(editor.course.id);
  useEffect(() => {
    currentId.current = editor.course.id;
  }, [editor.course.id]);
  const project = useMemo(
    () => replaceCourse(stored, editor.course) ?? stored,
    [stored, editor.course],
  );

  const open = useCallback(
    (course: Course, next: Project) => {
      structure.current += 1;
      if (courseDirty) setProjectDirty(true);
      setStored(next);
      dispatch({ type: 'replace-course', course });
      setBaseline(course);
    },
    [courseDirty],
  );

  return {
    project,
    session,
    editor,
    dispatch,
    dirty: projectDirty || courseDirty,
    select: (id) => {
      const course = project.courses.find((c) => c.id === id);
      if (!course || id === editor.course.id) return;
      open(course, project);
    },
    add: (course) => {
      const next = addCourse(project, course, editor.course.id);
      if (!next) return;
      setProjectDirty(true);
      open(course, next);
    },
    duplicate: (newId, now) => {
      const copy = duplicateCourse(editor.course, newId, now);
      const next = addCourse(project, copy, editor.course.id);
      if (!next) return;
      setProjectDirty(true);
      open(copy, next);
    },
    remove: (id) => {
      const next = removeCourse(project, id);
      if (!next) return;
      setProjectDirty(true);
      if (id === editor.course.id) {
        const neighbor = neighborAfterRemoval(project, id);
        if (neighbor) open(neighbor, next);
      } else {
        structure.current += 1;
        setStored(next);
      }
    },
    replaceProject: (next) => {
      const course = next.courses[0];
      if (!course) return;
      structure.current += 1;
      setProjectDirty(true);
      setStored(next);
      dispatch({ type: 'replace-course', course });
      setBaseline(course);
    },
    beginSave: () => structure.current,
    markSaved: (course, version) => {
      if (currentId.current === course.id) setBaseline(course);
      if (structure.current === version) setProjectDirty(false);
    },
  };
}
